import { useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { getVaspHoldings } from '../vaspEndpoints'
import { vaspKeys } from '../vaspQueryKeys'

/**
 * Every holding Ctrl-Alt has mirrored for one offering, with its title deeds
 * (LST-07).
 *
 * Published for the offerings module, which renders it on the offering-details
 * page — the only surface that holds an offering id (architecture-principles
 * §10, shared-endpoint hooks). An order row carries only a property id, which
 * is why this is keyed by offering and not by order.
 *
 * Gated on a truthy id so nothing is fetched before the route resolves. The
 * presigned deed link is deliberately NOT part of this query — it expires, so
 * it is minted per click by `useVaspTitleDeedDownloadMutation`.
 *
 * `queryKey` / `enabled` are `computed`, not getters: the installed
 * `@tanstack/vue-query` types accept `MaybeRefDeep` but not a bare getter for
 * `queryKey`. Same shape as `usePropertyOwnerBankAccountDetailQuery`.
 */
export function useVaspHoldingsQuery(offeringId: MaybeRefOrGetter<string | null | undefined>) {
  const idRef = computed(() => String(toValue(offeringId) ?? ''))
  return useQuery({
    queryKey: computed(() => vaspKeys.holdings(idRef.value)),
    queryFn: () => getVaspHoldings(idRef.value),
    enabled: computed(() => Boolean(idRef.value)),
  })
}
